import { useEffect, useRef, useState, memo } from 'react';

export interface LinkComponentProps {
  href?: string;
  children?: React.ReactNode;
  autoPlay?: boolean;
  isNewMessage?: boolean;
  id?: string;
  [key: string]: any;
}

// Create a global audio queue manager
const audioQueueManager = {
  queue: [] as string[],
  isPlaying: false,
  currentIndex: 0,
  currentAudio: null as HTMLAudioElement | null,
  processedAudios: new Set<string>(),

  addToQueue(audioSrc: string) {
    if (!this.processedAudios.has(audioSrc)) {
      this.queue.push(audioSrc);
      this.processedAudios.add(audioSrc);

      if (!this.isPlaying) {
        this.playNext();
      }
    }
  },

  playNext() {
    if (this.currentIndex >= this.queue.length) {
      this.stop();
      return;
    }

    this.isPlaying = true;

    const audioElement = document.querySelector(
      `audio[data-id="${this.queue[this.currentIndex]}"]`,
    ) as HTMLAudioElement;

    if (audioElement) {
      this.currentAudio = audioElement;

      // Remove any existing event listeners
      audioElement.onended = null;
      audioElement.onplay = null;
      audioElement.onpause = null;

      // Add event listeners for user interaction
      audioElement.onplay = () => {
        if (this.currentIndex > 0) {
          this.stop();
        }
      };

      audioElement.onpause = () => {
        this.stop();
      };

      audioElement.onended = () => {
        this.currentIndex++;
        this.playNext();
      };

      audioElement.play().catch(error => {
        this.currentIndex++;
        this.playNext();
      });
    } else {
      this.currentIndex++;

      this.playNext();
    }
  },

  stop() {
    if (this.currentAudio?.ended) {
      this.currentIndex++;
      this.playNext();
      return;
    }

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }

    this.queue = [];
    this.isPlaying = false;
    this.currentIndex = 0;
    this.currentAudio = null;
    this.processedAudios.clear();
  },
};

const LinkComponent = memo(function LinkComponent({
  autoPlay = false,
  isNewMessage = false,
  ...props
}: Readonly<LinkComponentProps>) {
  const isImageLink = props.href?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
  const isAudioLink = props.href?.match(/\.(mp3|wav|ogg|m4a)$/i);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (isAudioLink && autoPlay && isNewMessage) {
      audioQueueManager.addToQueue(`${props.href}-${props.id}`);
    }
  }, [isAudioLink, autoPlay, isNewMessage, props.href, props.id]);

  if (isImageLink) {
    return (
      <a href={props.href} target="_blank" rel="noopener noreferrer" {...props}>
        <img
          src={props.href}
          alt={props.children?.toString() ?? 'Image'}
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            objectFit: 'cover',
          }}
        />
      </a>
    );
  }

  if (isAudioLink) {
    return (
      <audio
        ref={audioRef}
        controls
        src={props.href}
        data-id={`${props.href}-${props.id}`}
        controlsList="nodownload"
        className="my-2 w-full max-w-full"
        style={{
          maxWidth: '100%',
        }}
      ></audio>
    );
  }

  return (
    <a
      {...props}
      aria-label={props.children?.toString()}
      target="_blank"
      rel="noopener noreferrer"
    />
  );
});

export { LinkComponent };
